import { TFunction, useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  MUIDataTable,
  Pill,
  TABLE_INITIAL_STATE,
  TableActionTypes,
  TableState,
  Typography,
  useDataTable,
  HtmlTrans,
  APIActions,
  CustomDialog,
} from 'web-library';
import React, { useEffect, useState } from 'react';

import { RouteComponentProps, useHistory } from 'react-router-dom';
import EditButton from 'web-library/components/buttons/EditButton';
import DeleteButton from 'web-library/components/buttons/DeleteButton';
import {
  generateAuthorityName,
  NOTIFICATION_FUNCTION_MAP,
} from 'web-library/models/AuthorityFunctions';
import {
  FunctionModule,
  PermissionType,
} from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import { stringToSemanticDisplay } from '../../../../utils/DateUtils';
import { useDeleteNotification } from '../../../../api-refactored/manage-notifications/useDeleteNotification';
import {
  NotificationListingResponseContentObject,
  usePostNotificationListing,
} from '../../../../api-refactored/manage-notifications/usePostNotificationListing';
import { useStyles } from '../index';
import { AnnouncementNotificationTitleUuid } from '../../../manageAnnouncements/ManageAnnouncementsListing';
import RouteKey from '../../../../routes/key';
import { useGetUserAuthorities } from '../../../../api-refactored/user/useGetUserAuthorities';

const { admin } = RouteKey.child;
const {
  manageNotifications,
  editEmailNotification,
  editSMSNotification,
  viewEmailNotification,
  viewSMSNotification,
} = RouteKey.admin.child;

const selectFeatureSubPathEditNotification = (feature: string) => {
  if (feature === 'EMAIL') {
    return editEmailNotification.route;
  }
  if (feature === 'SMS') {
    return editSMSNotification.route;
  }
  return null;
};

const selectFeatureSubPathViewNotification = (feature: string) => {
  if (feature === 'EMAIL') {
    return viewEmailNotification.route;
  }
  if (feature === 'SMS') {
    return viewSMSNotification.route;
  }
  return null;
};

const displayPill = (data: NotificationListingResponseContentObject[], dataIndex: number) => {
  const { status } = data[dataIndex];
  if (status === 'Sent') {
    return <Pill variant="positive" size="M" label={status} />;
  }
  if (status === 'Sending') {
    return <Pill variant="info" size="M" label={status} />;
  }
  return <>-</>;
};

const displayDateTimeUpdated = (
  data: NotificationListingResponseContentObject[],
  dataIndex: number,
  classes: Record<string,string>
) => (
  <>
    {stringToSemanticDisplay(data[dataIndex].sentDate)}
    <br />
    <Typography
      className={classes.updatedByText}
      variant="subtitle2"
    >{`Updated by ${data[dataIndex].updatedBy}`}</Typography>
  </>
);

interface TableColumnsProps {
  featureValue: string;
  state: TableState;
  history: RouteComponentProps['history'];
  classes: Record<string,string>;
  t: TFunction<'translation'>;
  setDeleteNotificationTitleUuid: React.Dispatch<
    React.SetStateAction<AnnouncementNotificationTitleUuid>
  >;
  setOpenDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
  authorities?: string[];
}

const tableColumns = ({
  featureValue,
  state,
  history,
  classes,
  t,
  setDeleteNotificationTitleUuid,
  setOpenDeleteModal,
  authorities,
}: TableColumnsProps) => [
  {
    name: 'title',
    label: t('announcementsNotifications->>Listing.Title'),
    options: {
      sort: false,
      customBodyRenderLite: (dataIndex: number) => (
        <Typography className={classes.titleDescription}>{state.data[dataIndex].title}</Typography>
      ),
    },
  },
  {
    name: 'sendDate',
    label: t('announcementsNotifications->>Listing.SendDate'),
    options: {
      customBodyRenderLite: (dataIndex: number) =>
        displayDateTimeUpdated(state.data, dataIndex, classes),
    },
  },
  {
    name: 'status',
    label: t('announcementsNotifications->>Listing.Status'),
    options: {
      sort: false,
      customBodyRenderLite: (dataIndex: number) => displayPill(state.data, dataIndex),
    },
  },
  {
    name: 'actions',
    label: t('announcementsNotifications->>Listing.Actions'),
    options: {
      sort: false,
      setCellProps: () => ({ style: { paddingTop: '18px' } }),
      customBodyRenderLite: (dataIndex: number) =>
        state.data[dataIndex].status === 'Sent' ? (
          <Button
            id="button-manage-notifications-view"
            className={classes.viewDetailsButton}
            onClick={() =>
              history.push(
                `/${admin}/${manageNotifications.route}/${
                  state.data[dataIndex].uuid
                }/${selectFeatureSubPathViewNotification(featureValue)}`,
                {
                  featureValue,
                  defaultTab: 0,
                  page: state.page,
                  pageSize: state.size,
                }
              )
            }
            variant="text"
            color="primary"
            size="small"
          >
            {t('announcementsNotifications->>Listing.ViewDetails')}
          </Button>
        ) : (
          <>
            {authorities?.includes(
              generateAuthorityName(
                'CP',
                FunctionModule.ADMIN,
                NOTIFICATION_FUNCTION_MAP[featureValue],
                PermissionType.EDIT
              )
            ) &&
              state.data[dataIndex].creator && (
                <Box className={`${classes.iconButtonContainer} ${classes.editButtonContainer}`}>
                  <EditButton
                    editHandler={() =>
                      history.push(
                        `/${admin}/${manageNotifications.route}/${
                          state.data[dataIndex].uuid
                        }/${selectFeatureSubPathEditNotification(featureValue)}`,
                        {
                          featureValue,
                          defaultTab: 0,
                          page: state.page,
                          pageSize: state.size,
                        }
                      )
                    }
                    label={t('announcementsNotifications->>Listing.Edit')}
                    typographyVariant="h6"
                  />
                </Box>
              )}
            {authorities?.includes(
              generateAuthorityName(
                'CP',
                FunctionModule.ADMIN,
                NOTIFICATION_FUNCTION_MAP[featureValue],
                PermissionType.DELETE
              )
            ) && (
              <Box className={classes.iconButtonContainer}>
                <DeleteButton
                  deleteHandler={() => {
                    setDeleteNotificationTitleUuid({
                      title: state.data[dataIndex].title,
                      uuid: state.data[dataIndex].uuid,
                    });
                    setOpenDeleteModal(true);
                  }}
                  label={t('announcementsNotifications->>Listing.Delete')}
                  typographyVariant="h6"
                />
              </Box>
            )}
          </>
        ),
    },
  },
];

interface NotificationDataTableProps {
  featureValue: string;
  featureDisplay: string;
}

const NotificationDataTable = ({ featureValue, featureDisplay }: NotificationDataTableProps) => {
  const { t } = useTranslation();

  const { classes } = useStyles();

  const history = useHistory<{
    featureValue: string;
    defaultTab: number;
    page: number;
    pageSize: number;
  }>();

  const dataFromViewNotification = history?.location?.state;

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const [
    deleteNotificationTitleUuid,
    setDeleteNotificationTitleUuid,
  ] = useState<AnnouncementNotificationTitleUuid>({
    title: '',
    uuid: '',
  });

  const { mutate: deleteNotification } = useDeleteNotification({
    onSuccess: () => {
      handleCloseDeleteModal();
      mutatePostNotificationListing({
        apiPathParam: {
          page: state.page,
          pageSize: state.size,
          sort: state.sortColumn,
          order: state.order,
        },
        notificationType: featureValue,
      });
    },
    uuid: deleteNotificationTitleUuid.uuid,
  });

  const { data: authorities } = useGetUserAuthorities();

  const handleCloseDeleteModal = () => setOpenDeleteModal(false);

  const handleDelete = () => deleteNotification(featureValue);

  const { state, dispatch: tableDispatch, tOptions } = useDataTable({
    ...TABLE_INITIAL_STATE,
    page: dataFromViewNotification?.page || 0,
    size: dataFromViewNotification?.pageSize || 5,
    sortColumn: 'sendDate',
    order: 'desc',
    search: featureValue,
  });

  const {
    mutate: mutatePostNotificationListing,
    isLoading: isLoadingNotificationListing,
  } = usePostNotificationListing({
    onSuccess: (data) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: data.content,
          count: data.totalElements,
        },
      } as TableActionTypes);
    },
  });

  useEffect(() => {
    if (featureValue) {
      mutatePostNotificationListing({
        apiPathParam: {
          page: state.page,
          pageSize: state.size,
          sort: state.sortColumn,
          order: state.order,
        },
        notificationType: featureValue,
      });
    }
  }, [
    mutatePostNotificationListing,
    state.order,
    state.page,
    state.size,
    state.sortColumn,
    featureValue,
  ]);

  return (
    <>
      <Box className={classes.dataTable}>
        <MUIDataTable
          isLoading={isLoadingNotificationListing}
          state={state}
          dispatch={tableDispatch}
          columns={tableColumns({
            featureValue,
            state,
            history,
            classes,
            t,
            setDeleteNotificationTitleUuid,
            setOpenDeleteModal,
            authorities,
          })}
          data={state.data}
          emptyDataLabel={t('announcementsNotifications->>Listing.EmptyDataTableMessage')}
          options={{
            ...tOptions,
            sort: true,
            pagination: true,
            sortOrder: {
              name: state.sortColumn || 'sendDate',
              direction: state.order || 'desc',
            },
          }}
          rowsPerPageOptions={[5, 10, 20, 40, 50]}
        />
      </Box>
      <CustomDialog
        open={openDeleteModal}
        title={t('announcementsNotifications->>Listing.DeleteNotificationTitle')}
        message={
          <HtmlTrans
            i18nKey="announcementsNotifications->>Listing.DeleteNotification"
            values={{ notificationType: featureDisplay, title: deleteNotificationTitleUuid.title }}
          />
        }
        action={
          <APIActions
            onClose={handleCloseDeleteModal}
            apiCall={handleDelete}
            cancelString={t('announcementsNotifications->>Listing.Cancel')}
            apiString={t('announcementsNotifications->>Listing.Proceed')}
          />
        }
      />
    </>
  );
};

export default NotificationDataTable;
